import { format } from "date-fns";
import { Calendar, Users } from "lucide-react";
import { TaskWithRelations } from "@/hooks/useTasks";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { formatProjectName } from "@/lib/format-project-name";

interface TaskCardProps {
  task: TaskWithRelations;
  onDragStart: (taskId: string) => void;
  onClick: () => void;
}

export function TaskCard({ task, onDragStart, onClick }: TaskCardProps) {
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = "move";
    onDragStart(task.id);
  };

  const isOverdue =
    task.due_date &&
    new Date(task.due_date) < new Date() &&
    task.state !== "done";

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onClick={onClick}
      className={cn(
        "bg-card border rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow min-w-0 overflow-hidden",
        "active:cursor-grabbing",
      )}
    >
      <h4
        className="mb-2 line-clamp-2 break-words text-sm font-medium"
        data-testid={`task-card-title-${task.id}`}
      >
        {task.title}
      </h4>

      {task.description && (
        <p className="mb-2 line-clamp-2 break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">
          {task.description.replace(/<[^>]*>/g, "")}
        </p>
      )}

      <div className="flex flex-wrap gap-1 mb-2">
        {task.projects.slice(0, 2).map((p) => (
          <Badge key={p.id} variant="outline" className="max-w-full text-xs">
            {formatProjectName(p.project.name, p.project.event_start_date)}
          </Badge>
        ))}
        {task.project_groups.slice(0, 2).map((pg) => (
          <Badge key={pg.id} variant="secondary" className="max-w-full text-xs">
            {pg.project_group.name}
          </Badge>
        ))}
        {task.projects.length + task.project_groups.length > 4 && (
          <Badge variant="outline" className="text-xs">
            +{task.projects.length + task.project_groups.length - 4}
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        {task.due_date && (
          <div
            className={cn(
              "flex items-center gap-1",
              isOverdue && "text-destructive",
            )}
          >
            <Calendar className="h-3 w-3" />
            {format(new Date(task.due_date), "dd.MM.yyyy")}
          </div>
        )}

        {task.assignees.length > 0 && (
          <div className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            {task.assignees.length}
          </div>
        )}
      </div>
    </div>
  );
}
